import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PreviewIcon from '@mui/icons-material/Preview';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import ProveedorSearch from '../../components/ProveedorSearch';
import { Proveedor } from '../../services/proveedorService';
import {
  importarPreciosExcel,
  PreciosProveedorImportResult,
} from '../../services/preciosProveedorService';
import InformesBackButton from '../../components/shared/InformesBackButton';

const ImportacionPreciosProveedor: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [archivo, setArchivo] = useState<File | null>(null);
  const [codigoProveedor, setCodigoProveedor] = useState('');
  const [proveedorNombre, setProveedorNombre] = useState('');
  const [openProveedorSearch, setOpenProveedorSearch] = useState(false);
  const [hojasDisponibles, setHojasDisponibles] = useState<string[]>([]);
  const [nombreHoja, setNombreHoja] = useState('');
  const [primeraFilaDatos, setPrimeraFilaDatos] = useState('1');
  const [ultimaFilaDatos, setUltimaFilaDatos] = useState('');
  const [colCodigo, setColCodigo] = useState('B');
  const [colCosto, setColCosto] = useState('G');
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState<PreciosProveedorImportResult | null>(null);

  const parseIntOr = (s: string, fallback: number) => {
    const n = parseInt(s, 10);
    return Number.isFinite(n) ? n : fallback;
  };

  const handleProveedorSelect = (proveedor: Proveedor) => {
    setCodigoProveedor(String(proveedor.nroProveedor));
    setProveedorNombre((proveedor.nombre ?? '').trim());
  };

  const handleProveedorNumeroChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const soloDigitos = e.target.value.replace(/\D/g, '');
    setCodigoProveedor(soloDigitos);
    setProveedorNombre('');
  };

  const ejecutar = async (soloSimular: boolean) => {
    if (!archivo) {
      enqueueSnackbar('Seleccione un archivo Excel (.xlsx).', { variant: 'warning' });
      return;
    }
    const hoja = nombreHoja.trim();
    if (!hoja) {
      enqueueSnackbar('Seleccione la hoja del libro.', { variant: 'warning' });
      return;
    }
    const prov = parseIntOr(codigoProveedor, NaN);
    if (!Number.isFinite(prov) || prov <= 0) {
      enqueueSnackbar('Ingrese un número de proveedor válido.', { variant: 'warning' });
      return;
    }

    setLoading(true);
    setResultado(null);
    try {
      const colCod = colCodigo.trim();
      const colCost = colCosto.trim();
      if (!colCod || !colCost) {
        enqueueSnackbar('Indique la columna del código de artículo del proveedor y la del costo (ej. B y G).', {
          variant: 'warning',
        });
        setLoading(false);
        return;
      }
      const primera = Math.max(1, parseIntOr(primeraFilaDatos, 1));
      const ultimaTrim = ultimaFilaDatos.trim();
      let ultima: number | undefined;
      if (ultimaTrim !== '') {
        ultima = parseIntOr(ultimaTrim, NaN);
        if (!Number.isFinite(ultima) || ultima < 1) {
          enqueueSnackbar('"Hasta fila" debe ser un número entero ≥ 1 o dejarse vacío.', { variant: 'warning' });
          setLoading(false);
          return;
        }
        if (ultima < primera) {
          enqueueSnackbar('"Hasta fila" no puede ser menor que la primera fila de datos.', { variant: 'warning' });
          setLoading(false);
          return;
        }
      }
      const res = await importarPreciosExcel(archivo, {
        codigoProveedor: prov,
        nombreHoja: hoja,
        primeraFilaDatos: primera,
        ...(ultima !== undefined ? { ultimaFilaDatos: ultima } : {}),
        columnaCodigoArticuloProveedor: colCod,
        columnaCosto: colCost,
        dryRun: soloSimular,
        guardarHistorico: !soloSimular,
      });
      setResultado(res);
      if (res.advertencias.length > 0) {
        res.advertencias.forEach((a) => enqueueSnackbar(a, { variant: 'info' }));
      }
      enqueueSnackbar(
        soloSimular
          ? `Simulación: ${res.costosPorProveedorActualizados} CPP / ${res.stockRecalculados} stock.`
          : `Proceso finalizado: ${res.costosPorProveedorActualizados} CPP actualizados.`,
        { variant: 'success' }
      );
    } catch (e: unknown) {
      type ErrBody = {
        message?: string;
        detail?: string;
        inner?: string;
        traceId?: string;
        diagnostic?: string;
      };
      let msg = 'Error al importar precios.';
      if (e && typeof e === 'object' && 'response' in e) {
        const data = (e as { response?: { data?: ErrBody } }).response?.data;
        if (data) {
          const parts = [data.message, data.detail, data.inner].filter(Boolean);
          if (parts.length > 0) {
            msg = parts.join(' — ');
            if (data.traceId) msg += ` [trace: ${data.traceId}]`;
          }
          // Respuesta 500 del API: ver cuerpo completo en consola (incluye diagnostic).
          console.error('Importación costos Base por código de proveedor — respuesta error', data);
        }
      } else {
        console.error('Importación costos Base por código de proveedor', e);
      }
      enqueueSnackbar(msg.length > 800 ? `${msg.slice(0, 800)}…` : msg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const cardSx = {
    bgcolor: '#ffffff',
    borderRadius: 3,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    overflow: 'hidden' as const,
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Stack spacing={2}>
        <Card sx={cardSx}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <InformesBackButton to="/inventario/mantenimiento-precios" useBrowserBack />
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  Actualización de Costos Base por código de proveedor desde Excel
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Busca por <strong>Codigo de Articulo del Proveedor</strong> en el excel y actualiza el <strong>Costo Base</strong> de cada producto, actualizando tambien su <strong>Precio de Venta Base</strong>
              </Typography>

              <TextField
                fullWidth
                required
                label="Nº proveedor"
                value={codigoProveedor}
                onChange={handleProveedorNumeroChange}
                inputProps={{ inputMode: 'numeric', 'aria-label': 'Número de proveedor' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setOpenProveedorSearch(true)}
                        edge="end"
                        size="small"
                        aria-label="Buscar proveedor"
                      >
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText={
                  proveedorNombre
                    ? `Nombre: ${proveedorNombre}`
                    : 'Ingrese el número manualmente o use la lupa para buscar por nombre u otros datos.'
                }
              />

              <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                {archivo ? archivo.name : 'Elegir archivo .xlsx'}
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xlsm"
                  onChange={async (e) => {
                    const input = e.target;
                    const f = input.files?.[0] ?? null;
                    setArchivo(f);
                    setHojasDisponibles([]);
                    setNombreHoja('');
                    if (!f) {
                      input.value = '';
                      return;
                    }
                    try {
                      const ab = await f.arrayBuffer();
                      const wb = XLSX.read(ab, { type: 'array', bookSheets: true });
                      const names = wb.SheetNames ?? [];
                      if (names.length === 0) {
                        enqueueSnackbar('El archivo no contiene hojas reconocibles.', { variant: 'warning' });
                        setArchivo(null);
                        input.value = '';
                        return;
                      }
                      setHojasDisponibles(names);
                      setNombreHoja(names[0]);
                    } catch (err) {
                      console.error('Lectura de hojas Excel', err);
                      enqueueSnackbar(
                        'No se pudo leer el archivo Excel. Verifique que sea .xlsx o .xlsm válido.',
                        { variant: 'error' }
                      );
                      setArchivo(null);
                      input.value = '';
                    }
                    input.value = '';
                  }}
                />
              </Button>

              <FormControl fullWidth disabled={hojasDisponibles.length === 0}>
                <InputLabel id="importacion-proveedor-hoja-label">Hoja</InputLabel>
                <Select
                  labelId="importacion-proveedor-hoja-label"
                  id="importacion-proveedor-hoja"
                  label="Hoja"
                  value={hojasDisponibles.includes(nombreHoja) ? nombreHoja : ''}
                  onChange={(ev) => setNombreHoja(ev.target.value as string)}
                >
                  {hojasDisponibles.map((n) => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {hojasDisponibles.length === 0
                    ? 'Elija un archivo para listar las hojas del libro.'
                    : 'Hojas detectadas en el archivo seleccionado.'}
                </FormHelperText>
              </FormControl>
              <TextField
                label="Número de la primera fila de datos"
                value={primeraFilaDatos}
                onChange={(e) => setPrimeraFilaDatos(e.target.value)}
                type="number"
                fullWidth
              />
              <TextField
                label="Hasta fila (última fila Excel a procesar)"
                value={ultimaFilaDatos}
                onChange={(e) => setUltimaFilaDatos(e.target.value)}
                type="number"
                fullWidth
                helperText="Opcional. Vacío = hasta el final del archivo. Inclusive (misma numeración que Excel)."
              />
              <TextField
                label="Letra de la columna código artículo proveedor"
                value={colCodigo}
                onChange={(e) => setColCodigo(e.target.value.toUpperCase())}
                fullWidth
                placeholder="Ej: B o AB (columna Excel)"
              />
              <TextField
                label="Letra de la columna costo"
                value={colCosto}
                onChange={(e) => setColCosto(e.target.value.toUpperCase())}
                fullWidth
                placeholder="Ej: G (letra de columna Excel)"
              />

              <FormControlLabel
                control={<Checkbox checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />}
                label="Solo simular: no realiza cambios en la base de datos"
              />
              <FormControlLabel
                control={<Checkbox checked disabled />}
                label="Guardar snapshot en CostosPorProveedorHistorico antes de realizar cambios"
              />

              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<PreviewIcon />}
                  disabled={loading}
                  onClick={() => ejecutar(true)}
                >
                  Simular
                </Button>
                <Button
                  variant="contained"
                  disabled={loading || dryRun}
                  onClick={() => ejecutar(false)}
                >
                  Procesar
                </Button>
              </Stack>
              {dryRun && (
                <Typography variant="caption" color="text.secondary">
                  Desactive &quot;Solo simular&quot; para habilitar Procesar.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>

        {resultado && (
          <Card sx={cardSx}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  fontWeight: 700,
                  color: '#1976d2',
                  mb: 2,
                }}
              >
                Resultado {resultado.dryRun ? '(simulación)' : ''}
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2">Filas leídas: {resultado.filasLeidas}</Typography>
                <Typography variant="body2">
                  CPP actualizados: {resultado.costosPorProveedorActualizados}
                </Typography>
                {(resultado.costosPorProveedorCreadosDesdeStock ?? 0) > 0 && (
                  <Typography variant="body2">
                    CPP nuevos (desde Stock): {resultado.costosPorProveedorCreadosDesdeStock}
                  </Typography>
                )}
                <Typography variant="body2">
                  Costos Base de Articulos actualizados: {resultado.stockRecalculados}
                </Typography>
                <Typography variant="body2">
                  Código de Articulo del Proveedor no encontrado: {resultado.sinCoincidenciaCostosPorProveedor}
                </Typography>
                <Typography variant="body2">Filas inválidas: {resultado.filasInvalidas}</Typography>
              </Stack>
              {resultado.erroresFila.length > 0 && (
                <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                  {resultado.erroresFila.slice(0, 50).map((e) => (
                    <li key={e}>
                      <Typography variant="caption">{e}</Typography>
                    </li>
                  ))}
                  {resultado.erroresFila.length > 50 && (
                    <Typography variant="caption">… y más</Typography>
                  )}
                </Box>
              )}
              {resultado.sinCoincidenciaDetalle.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Primeras filas sin CPP ({Math.min(20, resultado.sinCoincidenciaDetalle.length)} de{' '}
                    {resultado.sinCoincidenciaDetalle.length}):
                  </Typography>
                  <Box component="ul" sx={{ pl: 2 }}>
                    {resultado.sinCoincidenciaDetalle.slice(0, 20).map((d) => (
                      <li key={`${d.filaExcel}-${d.codigoArticuloProveedor}`}>
                        <Typography variant="caption">
                          Fila {d.filaExcel}: {d.codigoArticuloProveedor}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </Stack>

      <ProveedorSearch
        open={openProveedorSearch}
        onClose={() => setOpenProveedorSearch(false)}
        onSelect={handleProveedorSelect}
      />
    </Box>
  );
};

export default ImportacionPreciosProveedor;
